import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { motion } from 'framer-motion';
import { nip19 } from 'nostr-tools';
import { ZapButton } from '@/components/ZapButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  ArrowLeft,
  Disc3,
  Play,
  Pause,
  ExternalLink,
  Music,
  Clock,
  User,
  Share2,
  Copy,
  Check,
} from 'lucide-react';
import { LibrarySheet } from '@/components/LibrarySheet';
import { useAuthor } from '@/hooks/useAuthor';
import { useArtistTracks } from '@/hooks/useMusicTracks';
import { usePlayer } from '@/contexts/PlayerContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import { useToast } from '@/hooks/useToast';
import { genUserName } from '@/lib/genUserName';
import { formatDuration } from '@/lib/audioUtils';
import { cn } from '@/lib/utils';
import type { MusicTrack } from '@/hooks/useMusicTracks';

export function ArtistProfile() {
  const { npub } = useParams<{ npub: string }>();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const [copiedProfile, setCopiedProfile] = useState(false);
  const [copiedAlbum, setCopiedAlbum] = useState<string | null>(null);
  
  // Decode npub
  let pubkey: string | null = null;
  try {
    if (npub?.startsWith('npub1')) {
      const decoded = nip19.decode(npub);
      if (decoded.type === 'npub') {
        pubkey = decoded.data;
      }
    }
  } catch {
    pubkey = null;
  }

  const author = useAuthor(pubkey || '');
  const { data: tracks, isLoading } = useArtistTracks(pubkey || '', 50);
  const metadata = author.data?.metadata;
  
  const {
    currentTrack,
    setCurrentTrack,
    setPlaylist,
    isPlaying,
    togglePlay,
  } = usePlayer();

  const displayName = metadata?.name || metadata?.display_name || (pubkey ? genUserName(pubkey) : 'Unknown');
  const isOwnProfile = user?.pubkey === pubkey;
  const lnAddress = metadata?.lud16 || metadata?.lud06;

  // Get albums and genres
  const { albums, genres, totalDuration } = useMemo(() => {
    if (!tracks) return { albums: [], genres: [], totalDuration: 0 };
    
    const albumSet = new Set<string>();
    const genreSet = new Set<string>();
    let total = 0;

    tracks.forEach(track => {
      if (track.album) albumSet.add(track.album);
      track.genres.forEach(g => genreSet.add(g));
      total += track.duration || 0;
    });

    return {
      albums: Array.from(albumSet),
      genres: Array.from(genreSet),
      totalDuration: total,
    };
  }, [tracks]);

  useSeoMeta({
    title: `${displayName} - Cove`,
    description: metadata?.about || `Listen to music by ${displayName} on Cove`,
  });

  const handlePlayAll = () => {
    if (tracks && tracks.length > 0) {
      setPlaylist(tracks);
      setCurrentTrack(tracks[0]);
    }
  };

  const handleTrackSelect = (track: MusicTrack) => {
    if (tracks) {
      setPlaylist(tracks);
    }
    if (currentTrack?.id === track.id) {
      togglePlay();
    } else {
      setCurrentTrack(track);
    }
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/${npub}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedProfile(true);
      toast({ title: 'Profile link copied!' });
      setTimeout(() => setCopiedProfile(false), 2000);
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  };

  const handleShareAlbum = async (albumName: string) => {
    const url = `${window.location.origin}/${npub}?album=${encodeURIComponent(albumName)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedAlbum(albumName);
      toast({ title: `Album "${albumName}" link copied!` });
      setTimeout(() => setCopiedAlbum(null), 2000);
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  };

  if (!pubkey) {
    return (
      <div className="min-h-screen bg-black text-white">
        <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <span className="text-xl font-bold">Cove</span>
          </div>
        </header>
        <div className="container mx-auto px-4 py-20 text-center">
          <Disc3 className="h-16 w-16 mx-auto mb-4 text-zinc-700" />
          <h1 className="text-2xl font-bold mb-2">Invalid Profile</h1>
          <p className="text-zinc-400 mb-6">This doesn't look like a valid Nostr address</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 via-black to-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <Disc3 className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold">Cove</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {user && <LibrarySheet />}
            <LoginArea className="max-w-40" />
          </div>
        </div>
      </header>

      <main className="pb-32">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background */}
          {metadata?.banner ? (
            <div className="absolute inset-0 h-80">
              <img
                src={metadata.banner}
                alt=""
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black" />
            </div>
          ) : (
            <div className="absolute inset-0 h-80 bg-gradient-to-b from-zinc-800/50 to-transparent" />
          )}

          <div className="relative container mx-auto px-6 pt-12 pb-8">
            {/* Profile Info */}
            <div className="flex flex-col md:flex-row items-center md:items-end gap-6">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
              >
                <Avatar className="h-40 w-40 md:h-52 md:w-52 ring-4 ring-black shadow-2xl">
                  <AvatarImage src={metadata?.picture} />
                  <AvatarFallback className="text-5xl bg-zinc-800">
                    <User className="h-20 w-20 text-zinc-600" />
                  </AvatarFallback>
                </Avatar>
              </motion.div>

              <div className="flex-1 text-center md:text-left">
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <p className="text-sm text-zinc-400 mb-2">Artist</p>
                  <h1 className="text-4xl md:text-6xl font-bold mb-4">{displayName}</h1>
                  
                  {metadata?.about && (
                    <p className="text-zinc-400 mb-4 max-w-2xl line-clamp-2">
                      {metadata.about}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-zinc-400 mb-6">
                    <span className="flex items-center gap-1">
                      <Music className="h-4 w-4" />
                      {tracks?.length || 0} tracks
                    </span>
                    {albums.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Disc3 className="h-4 w-4" />
                        {albums.length} albums
                      </span>
                    )}
                    {totalDuration > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDuration(totalDuration)}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                    {tracks && tracks.length > 0 && (
                      <Button 
                        size="lg" 
                        onClick={handlePlayAll}
                        className="bg-primary hover:bg-primary/90 text-black font-bold rounded-full px-8"
                      >
                        <Play className="h-5 w-5 mr-2" fill="currentColor" />
                        Play
                      </Button>
                    )}
                    
                    {lnAddress && !isOwnProfile && tracks && tracks[0] && (
                      <ZapButton event={tracks[0].event} variant="outline" />
                    )}

                    {metadata?.website && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(metadata.website, '_blank')}
                        className="rounded-full"
                      >
                        <ExternalLink className="h-5 w-5" />
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleShareProfile}
                      className="rounded-full"
                    >
                      {copiedProfile ? (
                        <Check className="h-5 w-5 text-green-500" />
                      ) : (
                        <Share2 className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Genres */}
        {genres.length > 0 && (
          <section className="px-6 py-4">
            <div className="flex flex-wrap gap-2">
              {genres.slice(0, 8).map(genre => (
                <Badge key={genre} variant="secondary" className="capitalize bg-zinc-800">
                  {genre}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Albums Section */}
        {albums.length > 0 && (
          <section className="px-6 py-6">
            <h2 className="text-xl font-bold mb-4">Albums</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {albums.map(albumName => {
                const albumTracks = tracks?.filter(t => t.album === albumName) || [];
                const albumCover = albumTracks[0]?.coverImage;
                const trackCount = albumTracks.length;
                
                return (
                  <motion.div
                    key={albumName}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="group relative"
                  >
                    <div className="aspect-square rounded-xl overflow-hidden bg-zinc-800 mb-2">
                      {albumCover ? (
                        <img src={albumCover} alt={albumName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Disc3 className="h-12 w-12 text-zinc-600" />
                        </div>
                      )}
                      
                      {/* Share button overlay */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-8 w-8 bg-black/60 hover:bg-black/80 backdrop-blur-sm"
                          onClick={() => handleShareAlbum(albumName)}
                        >
                          {copiedAlbum === albumName ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Share2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <h3 className="font-medium text-sm truncate">{albumName}</h3>
                    <p className="text-xs text-zinc-500">{trackCount} track{trackCount !== 1 ? 's' : ''}</p>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* Tracks Section */}
        <section className="px-6 py-6">
          <h2 className="text-xl font-bold mb-4">Tracks</h2>

          {/* Track List Header */}
          <div className="grid grid-cols-[40px_1fr_80px] gap-4 px-4 py-2 border-b border-white/10 text-xs text-zinc-500 uppercase tracking-wider">
            <span>#</span>
            <span>Title</span>
            <span className="text-right">Duration</span>
          </div>

          {isLoading ? (
            <div className="space-y-2 mt-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="grid grid-cols-[40px_1fr_80px] gap-4 px-4 py-2">
                  <Skeleton className="h-4 w-4" />
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-4 w-12 ml-auto" />
                </div>
              ))}
            </div>
          ) : tracks && tracks.length > 0 ? (
            <div className="mt-2">
              {tracks.map((track, index) => (
                <motion.div
                  key={track.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => handleTrackSelect(track)}
                  className={cn(
                    'group grid grid-cols-[40px_1fr_80px] gap-4 px-4 py-3 rounded-md cursor-pointer',
                    'hover:bg-white/10 transition-colors',
                    currentTrack?.id === track.id && 'bg-white/10'
                  )}
                >
                  {/* Number / Play */}
                  <div className="flex items-center justify-center">
                    <span className={cn(
                      'text-sm tabular-nums group-hover:hidden',
                      currentTrack?.id === track.id ? 'text-primary' : 'text-zinc-500'
                    )}>
                      {currentTrack?.id === track.id && isPlaying ? (
                        <div className="flex items-center gap-0.5">
                          {[0, 1, 2].map((i) => (
                            <motion.div
                              key={i}
                              animate={{ height: [4, 12, 4] }}
                              transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.2 }}
                              className="w-0.5 bg-primary rounded-full"
                            />
                          ))}
                        </div>
                      ) : (
                        index + 1
                      )}
                    </span>
                    <button className="hidden group-hover:block">
                      {currentTrack?.id === track.id && isPlaying ? (
                        <Pause className="h-4 w-4" fill="currentColor" />
                      ) : (
                        <Play className="h-4 w-4" fill="currentColor" />
                      )}
                    </button>
                  </div>

                  {/* Title */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded overflow-hidden flex-shrink-0 bg-zinc-800">
                      {track.coverImage ? (
                        <img src={track.coverImage} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <Music className="h-4 w-4 text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={cn(
                        'text-sm font-medium truncate',
                        currentTrack?.id === track.id && 'text-primary'
                      )}>
                        {track.title}
                      </p>
                      {track.album && (
                        <p className="text-xs text-zinc-500 truncate">{track.album}</p>
                      )}
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="flex items-center justify-end">
                    <span className="text-sm text-zinc-500 tabular-nums">
                      {track.duration > 0 ? formatDuration(track.duration) : '—'}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Music className="h-12 w-12 mx-auto mb-4 text-zinc-700" />
              <p className="text-zinc-500">No tracks yet</p>
            </div>
          )}
        </section>
      </main>

    </div>
  );
}
